import { useState, useEffect } from "react"
import { Button, Card, Form, OverlayTrigger, Tooltip } from "react-bootstrap"
import { IconPlus, IconTrash, IconGripVertical } from "@tabler/icons-react"
import img from "../../Utils/assets/img/color_palette.png"
import DeleteModal from "../DeleteModal"

export const StageCard = ({
  stage: initialStage,
  listeners,
  isEditing,
  onChangeField,
  onDelete,
  onAddLeft,
  onAddRight,
  deleteStageConfirm,
  setDeleteStageConfirm,
  onRequestDelete
}) => {
  const [stage, setStage] = useState(initialStage)
  const [showAddButtons, setShowAddButtons] = useState(false)
  const [displayProbability, setDisplayProbability] = useState(String(initialStage.probability))
  const [showColorPicker, setShowColorPicker] = useState(false)
  // Default colors for the palette
  const colorOptions = ["#ffffff", "#ffe8cd", "#e4e6f9", "#d8f8ff", "#cbffe8", "#f3f3f3", "#ffe0fd", "#ffe0e0"]

  // FIX: Only update local state when stage_id or id changes, not on every prop change
  useEffect(() => {
    const stageId = initialStage.id
    const currentId = stage.id

    // Only reset if it's a different stage
    if (stageId !== currentId) {
      setStage(initialStage)
      setDisplayProbability(String(initialStage.probability))
    }
  }, [initialStage.stage_id, initialStage.id])

  const handleFieldChange = (field, value) => {
    let processedValue = value
    if (field === "probability") {
      processedValue = Math.max(0, Math.min(100, value))
    } else if (field === "rotting_days") {
      processedValue = Math.max(0, value)
    }

    // FIX: Update local state first
    setStage(prevStage => ({ ...prevStage, [field]: processedValue }))

    // FIX: Pass the color along with other field updates
    const stageId = stage.stage_id || stage.id
    if (field === "color") {
      onChangeField(stageId, field, processedValue, processedValue)
    } else {
      onChangeField(stageId, field, processedValue, stage.color)
    }
  }

  const handleColorSelect = (color) => {
    handleFieldChange("color", color)
    setShowColorPicker(false)
  }

  const handleToggleRotting = () => {
    const newRottingEnabled = !stage.rotting_enabled
    const newRottingDays = newRottingEnabled ? (stage.rotting_days > 0 ? stage.rotting_days : 1) : null

    // Update both fields together
    setStage(prevStage => ({
      ...prevStage,
      rotting_enabled: newRottingEnabled,
      rotting_days: newRottingDays
    }))

    const stageId = stage.stage_id || stage.id
    onChangeField(stageId, "rotting_enabled", newRottingEnabled, stage.color)
    onChangeField(stageId, "rotting_days", newRottingDays, stage.color)
  }

  const handleProbabilityFocus = () => {
    if (displayProbability === "0") {
      setDisplayProbability("")
    }
  }

  const handleProbabilityBlur = () => {
    if (displayProbability === "") {
      setDisplayProbability("0")
      handleFieldChange("probability", 0)
    } else {
      const value = Math.min(100, Math.max(0, displayProbability))
      setDisplayProbability(String(value))
      handleFieldChange("probability", value)
    }
  }

  const handleProbabilityChange = (e) => {
    const value = e.target.value
    if (value === "" || (!isNaN(value) && value <= 100)) {
      setDisplayProbability(value)
    }
  }

  // Render color indicator based on selected color
  const renderColorIndicator = () => {
    if (stage.color) {
      // If color is selected, show a colored circle
      return (
        <div
          className="rounded-circle border"
          style={{
            width: "24px",
            height: "24px",
            backgroundColor: stage.color,
            border: "2px solid #dee2e6",
            cursor: "pointer"
          }}
        />
      )
    } else {
      // If no color selected, show the default palette icon
      return (
        <img
          src={img}
          alt="color palette"
          width="24"
          height="24"
          style={{ cursor: "pointer" }}
        />
      )
    }
  }

  if (stage.action === "delete") return null

  return (
    <div
      className="stage-column position-relative me-3"
      style={{ minWidth: "260px", margin: "1rem" }}
      onMouseEnter={() => setShowAddButtons(true)}
      onMouseLeave={() => setShowAddButtons(false)}
    >
      {showAddButtons && !listeners?.active && (
        <>
          <Button
            variant="light"
            size="sm"
            className="position-absolute start-0 top-50 translate-middle shadow-sm"
            style={{
              zIndex: 10,
              transform: "translateX(-50%) translateY(-50%)",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              padding: "0",
              backgroundColor: "#7a3a5e",
              borderColor: "#7a3a5e",
              color: "white",
            }}
            onClick={(e) => {
              e.stopPropagation()
              onAddLeft(Number.parseInt(stage.sequence))
            }}
          >
            <IconPlus size={16} />
          </Button>

          <Button
            variant="light"
            size="sm"
            className="position-absolute top-50 shadow-sm"
            style={{
              right: "-18px",
              zIndex: 10,
              transform: "translateY(-50%)",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              padding: "0",
              backgroundColor: "#7a3a5e",
              borderColor: "#7a3a5e",
              color: "white",
            }}
            onClick={(e) => {
              e.stopPropagation()
              onAddRight(Number.parseInt(stage.sequence))
            }}
          >
            <IconPlus size={16} />
          </Button>
        </>
      )}

      <Card
        className="h-100 border-1"
        style={{
          minHeight: "200px",
        }}
      >
        <Card.Header
          className="d-flex justify-content-between align-items-center p-3 px-0"
          {...listeners}
          style={{
            cursor: listeners ? "grab" : "default",
            backgroundColor: "#f8f9fa",
            borderBottom: "1px solid #cfd0d4",
          }}
        >
          <span className="fw-bold text-dark">{stage.stage_name}</span>

          <div className="d-flex align-items-center">
            {/* Color Palette with Tooltip */}
            <div className="position-relative">
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Set stage color</Tooltip>}
              >
                <button
                  type="button"
                  className="btn btn-sm p-0 border-0 bg-transparent"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                >
                  {renderColorIndicator()}
                </button>
              </OverlayTrigger>

              {/* Color Picker Popover */}
              {showColorPicker && (
                <div
                  className="position-absolute end-0 mt-1 p-2 rounded shadow-lg bg-white"
                  style={{
                    zIndex: 1050,
                    width: "150px",
                    border: "1px solid #dee2e6"
                  }}
                >
                  <div className="d-flex flex-wrap gap-1">
                    {colorOptions.map((color, index) => (
                      <button
                        key={index}
                        type="button"
                        className="btn btn-sm p-0 border-1 rounded-circle"
                        style={{
                          width: "24px",
                          height: "24px",
                          border: "2px solid black",
                          backgroundColor: color,
                          boxShadow: stage.color === color ? "0 0 0 2px #000" : "none"
                        }}
                        onClick={() => handleColorSelect(color)}
                        title={color}
                      />
                    ))}

                    {/* Remove color option */}
                    <button
                      type="button"
                      className="btn btn-sm p-0 border-1 rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "white",
                        borderColor: "#dc3545"
                      }}
                      onClick={() => handleColorSelect(null)}
                      title="Remove color"
                    >
                      <IconTrash size={12} className="text-danger" />
                    </button>
                  </div>

                  {/* Close button */}
                  <div className="text-center mt-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowColorPicker(false)}
                      className="w-100"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {listeners && <IconGripVertical size={16} className="text-muted" />}
          </div>
        </Card.Header>

        <Card.Body
          style={{
            minHeight: "320px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
          className="p-3 px-0"
        >
          <div>
            {isEditing && (
              <div className="mb-3">
                <Form.Label className="small fw-semibold text-muted">Stage Name</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={stage.stage_name}
                  onChange={(e) => handleFieldChange("stage_name", e.target.value)}
                  className={`border-1 ${stage.nameError ? "border-danger" : ""}`}
                  style={{ borderColor: stage.nameError ? "#dc3545" : "#e9ecef" }}
                />
                {stage.nameError && <div className="text-danger small mt-1">{stage.nameError}</div>}
              </div>
            )}

            <div className="mb-3">
              <Form.Label className="small fw-semibold text-muted">Probability (%)</Form.Label>
              {isEditing ? (
                <Form.Control
                  type="number"
                  size="sm"
                  value={displayProbability}
                  onChange={handleProbabilityChange}
                  onFocus={handleProbabilityFocus}
                  onBlur={handleProbabilityBlur}
                  min="0"
                  max="100"
                  className="border-1"
                  style={{ borderColor: "#e9ecef" }}
                />
              ) : (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small text-muted fw-semibold">Probability</span>
                  <span className="fw-bold" style={{ color: "#7a3a5e" }}>
                    {stage.probability || 0}%
                  </span>
                </div>
              )}
            </div>

            <div className="mb-3">
              <Form.Check
                type="switch"
                label={<small className="text-muted">Rotting in (days)</small>}
                checked={stage.rotting_enabled}
                onChange={handleToggleRotting}
                className="mb-2"
              />
              {isEditing && (
                <Form.Control
                  type="number"
                  size="sm"
                  value={stage.rotting_days || ""}
                  onChange={(e) => handleFieldChange("rotting_days", e.target.value)}
                  min="1"
                  className="border-2 mt-2"
                  style={{
                    borderColor: "#e9ecef",
                    visibility: stage.rotting_enabled ? "visible" : "hidden",
                    height: "32px",
                  }}
                />
              )}
            </div>
          </div>

          {isEditing && (
            <div className="text-center pt-2 border-top">
              <Button
                variant="link"
                size="sm"
                className="text-danger p-0 text-decoration-none"
                onClick={(e) => {
                  e.stopPropagation()
                  onRequestDelete(stage.id)
                }}
              >
                <IconTrash size={14} className="me-1" />
                Delete Stage
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}